/* ============================================================
   MARCOZO CART UPSELL — Logic v18 (cart-state aware + robust detection)
   Save as: assets/cart-upsell.js
   ============================================================ */

(function () {
  'use strict';

  // Version stamp for cache busting visibility
  console.log('[Marcozo Upsell] Loaded v44 (progress bar requires desktop input AND ≥768px viewport)');

  const CONFIG = {
    discountCode: 'CHAINBUNDLE20',
    discountAmountCents: 1500,
    maxRecommendations: 3,
    chainProductTypes: ['Chain', 'Chains', 'Necklace'],
    pendantProductTypes: ['Pendant', 'Pendants'],
    drawerRefreshEvents: [
      'theme:cart:refresh',
      'theme:cart:change',
      'theme:cart:update',
      'theme:cart-drawer:open',
      'theme:cart-drawer:show',
      'theme:product:added',
    ],
    cartDrawerSelector: '#CartDrawer, [data-cart-drawer], .cart-drawer',
    compactLayoutThreshold: 2,
    debug: false, // Set to true if you need to debug
  };

  const log = (...args) => CONFIG.debug && console.log('[Marcozo Upsell]', ...args);
  const warn = (...args) => CONFIG.debug && console.warn('[Marcozo Upsell]', ...args);

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  // ============================================================
  // Performance caches (module scope — survive across renders)
  // ============================================================

  // Parsed universal map cache — JSON.parse once per page load, not per render
  const _mapCache = new Map();

  // Product fetch cache — /products/{handle}.js is stable per session
  const _productCache = new Map();

  // In-flight product fetches — dedupe parallel calls for same handle
  const _inflightProducts = new Map();

  // ============================================================
  // Fetch helpers
  // ============================================================

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return await res.json();
    } catch (err) { log('fetchCart error', err); return null; }
  }

  async function fetchProductByHandle(handle) {
    // Return cached product immediately
    if (_productCache.has(handle)) return _productCache.get(handle);
    // Dedupe concurrent fetches for the same handle
    if (_inflightProducts.has(handle)) return _inflightProducts.get(handle);

    const promise = (async () => {
      try {
        const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) {
          _productCache.set(handle, null);
          return null;
        }
        const product = await res.json();
        _productCache.set(handle, product);
        return product;
      } catch (err) {
        log('fetchProductByHandle error', handle, err);
        _productCache.set(handle, null);
        return null;
      } finally {
        _inflightProducts.delete(handle);
      }
    })();

    _inflightProducts.set(handle, promise);
    return promise;
  }

  async function fetchShopifyRecommendations(productId, limit = 4) {
    try {
      const res = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=complementary`,
        { headers: { Accept: 'application/json' } }
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.products || [];
    } catch (err) { log('fetchShopifyRecommendations error', err); return []; }
  }

  // Final fallback: pull chains directly from a collection or by product type via Shopify search
  async function fetchChainsFromCatalog(limit = 4) {
    try {
      // Try the "chains" collection first (most stores have one)
      const res = await fetch(`/collections/chains/products.json?limit=${limit}`, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.products && data.products.length > 0) {
          log('Chains collection fallback hit:', data.products.length, 'chains');
          return data.products;
        }
      }
    } catch (err) { log('chains collection fetch error', err); }

    // Fallback to product search by type
    try {
      const res = await fetch(`/search/suggest.json?q=chain&resources[type]=product&resources[limit]=${limit}`, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        const products = data.resources?.results?.products || [];
        log('Search suggest fallback hit:', products.length, 'products');
        return products.filter(p => /chain|necklace/i.test(p.title || '') || /chain|necklace/i.test(p.product_type || ''));
      }
    } catch (err) { log('search suggest fetch error', err); }

    return [];
  }

  // Final fallback for State 2: try multiple collection paths to find pendants
  async function fetchPendantsFromCatalog(limit = 4, excludeHandles = []) {
    // Try multiple collection paths since stores name them differently
    const collectionPaths = [
      '/collections/pendants/products.json',
      '/collections/all-pendants/products.json',
      '/collections/necklaces/products.json',
      '/collections/all/products.json', // last resort: pull from "all"
    ];

    for (const path of collectionPaths) {
      try {
        const res = await fetch(`${path}?limit=${limit + excludeHandles.length + 5}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) continue;
        const data = await res.json();
        if (!data.products || data.products.length === 0) continue;

        // Filter out items in cart, and prefer pendants if we can detect them
        let filtered = data.products.filter(p => !excludeHandles.includes(p.handle));
        // For "all" path, filter to items that look like pendants (not chains)
        if (path.includes('/all/')) {
          filtered = filtered.filter(p => looksLikePendant({
            product_title: p.title,
            handle: p.handle,
            product_type: p.product_type,
          }));
        }
        if (filtered.length > 0) {
          log(`Pendant fallback hit (${path}):`, filtered.length, 'items');
          return filtered.slice(0, limit);
        }
      } catch (err) { log(`Pendant collection error (${path}):`, err); }
    }
    return [];
  }

  // ============================================================
  // Maps + type detection
  // ============================================================

  function getMap(scriptId) {
    if (_mapCache.has(scriptId)) return _mapCache.get(scriptId);
    const el = document.getElementById(scriptId);
    if (!el) {
      _mapCache.set(scriptId, {});
      return {};
    }
    try {
      const parsed = JSON.parse(el.textContent);
      _mapCache.set(scriptId, parsed);
      return parsed;
    } catch (err) {
      log(`Invalid JSON in ${scriptId}`, err);
      _mapCache.set(scriptId, {});
      return {};
    }
  }

  function isType(item, types) {
    return types.some(t => (item.product_type || item.type || '').toLowerCase() === t.toLowerCase());
  }

  // Fallback type detection — checks title keywords if product_type is missing/wrong
  function looksLikeChain(item) {
    if (isType(item, CONFIG.chainProductTypes)) return true;
    const title = (item.product_title || item.title || item.product_handle || item.handle || '').toLowerCase();
    return /\b(chain|necklace|cuban|franco|rope|figaro|box|snake|herringbone|curb)\b/.test(title);
  }

  function looksLikePendant(item) {
    if (isType(item, CONFIG.pendantProductTypes)) return true;
    const title = (item.product_title || item.title || item.product_handle || item.handle || '').toLowerCase();
    // "necklace" is ambiguous — could be either. But pendants in this store often have "pendant" or are named after symbols
    return /\b(pendant|trident|lion|spartan|gladiator|ankh|cross|skull|helmet|wolf|eagle|dragon)\b/.test(title);
  }

  const isPendant = item => looksLikePendant(item);
  const isChain   = item => looksLikeChain(item);

  // ============================================================
  // Cart state detection
  // ============================================================

  function detectCartState(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return { state: 'empty', items: [] };
    }

    const totalItems = cart.items.reduce((sum, i) => sum + i.quantity, 0);
    const compact = totalItems >= CONFIG.compactLayoutThreshold;

    // Count pendants and chains (quantity-aware) for discount stack logic.
    // Your CHAINBUNDLE20 is unlimited — every pendant+chain pair earns $15 off.
    // So with 2 pendants + 1 chain, there's still 1 unmatched pendant looking
    // for its chain, and recommending another chain should still show "Save $15".
    let pendant = null;
    let chain = null;
    let pendantCount = 0;
    let chainCount = 0;

    for (const item of cart.items) {
      const titleLower = (item.product_title || item.title || '').toLowerCase();
      const explicitChain = /\b(chain|cuban|franco|rope|figaro|box|snake|herringbone|curb)\b/.test(titleLower);
      const qty = item.quantity || 1;

      if (explicitChain) {
        chainCount += qty;
        if (!chain) chain = item;
      } else if (looksLikePendant(item)) {
        pendantCount += qty;
        if (!pendant) pendant = item;
      } else if (looksLikeChain(item)) {
        chainCount += qty;
        if (!chain) chain = item;
      }
    }

    // Number of unmatched pendant+chain "sets waiting to be completed":
    //   2 pendants + 1 chain → 1 unmatched pendant → adding chain unlocks $15
    //   1 pendant + 2 chains → 1 unmatched chain   → adding pendant unlocks $15
    //   2 pendants + 2 chains → balanced           → no extra discount available
    const unmatchedPendants = Math.max(0, pendantCount - chainCount);
    const unmatchedChains = Math.max(0, chainCount - pendantCount);

    // State naming for analytics — describes the dominant cart pattern
    let stateName;
    if (pendant && chain) stateName = compact ? 'state3' : 'state2';
    else if (pendant) stateName = 'state1';
    else if (chain) stateName = 'state4';
    else stateName = 'state5'; // ring-only, bracelet-only, mixed without pendant/chain

    return {
      state: stateName,
      pendant,
      chain,
      pendantCount,
      chainCount,
      unmatchedPendants,
      unmatchedChains,
      items: cart.items,
      compact,
    };
  }

  // ============================================================
  // UNIVERSAL recommendation builder
  // Works for any cart contents — pendants, chains, rings, bracelets,
  // or any combination. Uses universal co-purchase map keyed by every
  // product handle.
  // ============================================================

  async function buildRecommendations(state) {
    const map = getMap('marcozo-universal-map');
    const inCartHandles = state.items.map(i => i.handle);

    // Bundle discount eligibility — your CHAINBUNDLE20 is UNLIMITED and applies
    // $15 off the chain for every pendant+chain pair in the cart. So we recommend
    // discount messaging based on which side has the surplus:
    //
    //   Cart has more pendants than chains (e.g. 2 pendants + 1 chain):
    //     → recommending a chain completes another set → show $15 off badge
    //   Cart has more chains than pendants (e.g. 1 chain alone, or 2 chains + 1 pendant):
    //     → recommending a pendant completes another set → show "unlocks $15 off" copy
    //   Balanced (e.g. 2 pendants + 2 chains): all sets complete → no badge
    //
    // This works for any quantity, matching the unlimited nature of the discount.
    function isDiscountEligible(recCategory) {
      if (recCategory === 'chain' && state.unmatchedPendants > 0) return true;
      if (recCategory === 'pendant' && state.unmatchedChains > 0) return true;
      return false;
    }

    // ============================================================
    // Determine cart's dominant material(s) for material-match boost
    // ============================================================
    const cartMaterials = new Set();
    for (const item of state.items) {
      const t = (item.product_title || item.title || '').toLowerCase();
      if (t.includes('white gold')) cartMaterials.add('white_gold');
      else if (t.includes('ancient gold')) cartMaterials.add('ancient_gold');
      else if (t.includes('sterling silver') || t.includes('silver')) cartMaterials.add('silver');
      else if (t.includes('gold')) cartMaterials.add('gold');
      else if (t.includes('black')) cartMaterials.add('black');
    }
    log('Cart materials:', [...cartMaterials]);

    // ============================================================
    // LTV-based category boost — your real data shows ring/bracelet
    // buyers have 1.5-3x higher LTV than chain/pendant buyers
    // ============================================================
    const CATEGORY_BOOST = {
      'ring': 1.20,
      'bracelet': 1.15,
      'chain': 1.00,
      'pendant': 1.00,
    };

    // ============================================================
    // Aggregate co-purchases across all cart items, weighted intelligently
    // ============================================================
    const aggregated = new Map(); // handle → { score, count, category, material }

    for (const item of state.items) {
      const recs = map[item.handle] || [];
      for (const rec of recs) {
        if (inCartHandles.includes(rec.handle)) continue;

        // Base score: frequency × pair_count (real co-purchase strength)
        let score = (rec.frequency || 0) * (rec.pair_count || 1);

        // Material match boost: 98.8% of bundles share material in your data
        if (rec.material && cartMaterials.has(rec.material)) {
          score *= 1.5;
        } else if (rec.material && rec.material !== 'unknown' && cartMaterials.size > 0) {
          // Strong penalty for material mismatch — your customers don't mix
          score *= 0.3;
        }

        // Category boost (high-LTV categories)
        score *= (CATEGORY_BOOST[rec.category] || 1.0);

        const existing = aggregated.get(rec.handle) || {
          score: 0, count: 0, category: rec.category, material: rec.material,
        };
        existing.score += score;
        existing.count = Math.max(existing.count, rec.pair_count || 0);
        aggregated.set(rec.handle, existing);
      }
    }

    // ============================================================
    // Diversity: avoid 3 cards of the same category when possible
    // ============================================================
    const sortedAll = [...aggregated.entries()]
      .sort((a, b) => b[1].score - a[1].score);

    log(`Top scored co-purchases (top 8):`,
      sortedAll.slice(0, 8).map(([h, d]) =>
        `${h} [${d.category}/${d.material}] score=${d.score.toFixed(1)}`));

    // Take top 3 with diversity preference
    const picked = [];
    const categoriesPicked = new Map(); // category → count

    // First pass: take #1 always, then prefer different categories
    for (const [handle, data] of sortedAll) {
      if (picked.length >= CONFIG.maxRecommendations) break;
      const catCount = categoriesPicked.get(data.category) || 0;
      // Allow max 2 of the same category in top 3
      if (catCount >= 2 && picked.length < CONFIG.maxRecommendations - 1) continue;
      picked.push([handle, data]);
      categoriesPicked.set(data.category, catCount + 1);
    }

    // Fill remaining slots with anything if diversity-pass didn't fill
    if (picked.length < CONFIG.maxRecommendations) {
      for (const [handle, data] of sortedAll) {
        if (picked.length >= CONFIG.maxRecommendations) break;
        if (picked.some(([h]) => h === handle)) continue;
        picked.push([handle, data]);
      }
    }

    log('Final picks:', picked.map(([h, d]) => `${h} [${d.category}]`));

    // ============================================================
    // INSTANT RENDER PATH
    // If picks have embedded preview data (image+price+title), build
    // synthetic product objects that renderCard can use immediately.
    // The /products/{handle}.js fetch happens in the background and
    // hydrates the card's variant selector when it resolves.
    // ============================================================
    const recs = [];
    const hydrationPromises = [];

    for (const [handle, data] of picked) {
      const recDiscount = isDiscountEligible(data.category);
      const cached = _productCache.get(handle);
      if (cached) {
        // Already fetched — render with full data
        recs.push({
          product: cached,
          source: 'universal-copurchase',
          applyDiscount: recDiscount,
          category: data.category,
        });
        continue;
      }

      // Build synthetic product from embedded preview data
      const previewData = {
        handle: handle,
        title: data.title,
        featured_image: data.image,
        images: data.image ? [data.image] : [],
        price: data.priceCents || 0,
        compare_at_price: data.compareAtCents || null,
        product_type: data.category || '',
        // Single fake variant so renderCard works; will be replaced on hydration
        variants: [{
          id: null,  // will trigger fetch on add-to-cart
          available: true,
          price: data.priceCents || 0,
          compare_at_price: data.compareAtCents || null,
          title: 'Default',
          option1: null,
        }],
        _isPreview: true,  // flag for hydration
      };

      recs.push({
        product: previewData,
        source: 'universal-copurchase',
        applyDiscount: recDiscount,
        category: data.category,
      });

      // Kick off background hydration — replace card with full product when ready
      hydrationPromises.push(fetchProductByHandle(handle).then(full => ({ handle, full })));
    }

    // Stash hydration promises on the rec list so renderUpsell can finish them
    recs._hydrationPromises = hydrationPromises;

    // ============================================================
    // FALLBACK CHAIN — never let the upsell render empty
    // ============================================================

    if (recs.length < CONFIG.maxRecommendations) {
      log(`Only ${recs.length} from co-purchase, falling back to Shopify recs...`);

      // Parallelize Shopify rec fetches across all cart items
      const shopifyPromises = state.items.map(item =>
        fetchShopifyRecommendations(item.product_id, CONFIG.maxRecommendations + 2)
      );
      const shopifyResults = await Promise.all(shopifyPromises);

      // Flatten and dedupe across cart items
      const seen = new Set([
        ...inCartHandles,
        ...recs.map(r => r.product.handle),
      ]);
      for (const productList of shopifyResults) {
        for (const product of productList) {
          if (recs.length >= CONFIG.maxRecommendations) break;
          if (seen.has(product.handle)) continue;
          seen.add(product.handle);
          recs.push({ product, source: 'shopify', applyDiscount: false });
        }
        if (recs.length >= CONFIG.maxRecommendations) break;
      }
    }

    // Last-resort: pull from store catalog
    if (recs.length < CONFIG.maxRecommendations) {
      log('Falling back to catalog browse');
      const fallbackProducts = await fetchFallbackProducts(
        CONFIG.maxRecommendations - recs.length + 2,
        inCartHandles
      );
      for (const productSummary of fallbackProducts) {
        if (recs.length >= CONFIG.maxRecommendations) break;
        if (recs.some(r => r.product.handle === productSummary.handle)) continue;
        const product = await fetchProductByHandle(productSummary.handle);
        if (product) recs.push({ product, source: 'catalog', applyDiscount: false });
      }
    }

    return recs;
  }

  // Generic catalog fallback — tries multiple collection paths
  async function fetchFallbackProducts(limit, excludeHandles = []) {
    const collectionPaths = [
      '/collections/best-sellers/products.json',
      '/collections/bestsellers/products.json',
      '/collections/featured/products.json',
      '/collections/all/products.json',
    ];
    for (const path of collectionPaths) {
      try {
        const res = await fetch(`${path}?limit=${limit + excludeHandles.length + 5}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) continue;
        const data = await res.json();
        if (!data.products || data.products.length === 0) continue;
        const filtered = data.products.filter(p => !excludeHandles.includes(p.handle));
        if (filtered.length > 0) {
          log(`Catalog fallback hit (${path}):`, filtered.length, 'items');
          return filtered.slice(0, limit);
        }
      } catch (err) { log(`Catalog fetch error (${path}):`, err); }
    }
    return [];
  }

  // ============================================================
  // Render
  // ============================================================

  function formatMoney(cents) {
    const currency = $('[data-marcozo-upsell]')?.dataset.shopCurrency || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  }

  function renderCard(rec, opts = {}) {
    const { compact = false, applyDiscount = false } = opts;
    const product = rec.product;
    const isPreview = !!product._isPreview;
    const variants = product.variants || [];
    const firstAvailable = variants.find(v => v.available) || variants[0];

    // For preview cards, pricing comes from product.price; for real products from variant.price
    const originalPrice = isPreview
      ? (product.price || 0)
      : (firstAvailable ? firstAvailable.price : 0);

    if (!isPreview && !firstAvailable) return null;

    // Discount display logic — drives strikethrough vs unlock-note copy.
    //
    // Your CHAINBUNDLE20 always lands on the CHAIN line item, regardless of
    // which item triggered the bundle. So:
    //
    //   Recommending a CHAIN (cart has unmatched pendants):
    //     → adding this completes a set → discount lands HERE
    //     → show strikethrough on the chain card ($59.99 → $39.99) ✓
    //
    //   Recommending a PENDANT (cart has unmatched chains):
    //     → adding this completes a set → discount lands on the chain ALREADY in cart
    //     → show full pendant price + "unlocks $15 off your chain" copy
    //
    // Anything else (bracelets, rings, balanced cart): no discount messaging.
    const isChainRec = rec.category === 'chain';
    const isPendantRec = rec.category === 'pendant';
    const showStrikethrough = applyDiscount && isChainRec;
    const showUnlockNote = applyDiscount && isPendantRec;

    const salePrice = showStrikethrough
      ? Math.max(originalPrice - CONFIG.discountAmountCents, 0)
      : originalPrice;
    const displayPrice = showStrikethrough ? salePrice : originalPrice;

    const card = document.createElement('div');
    card.className = `marcozo-upsell__card${compact ? ' marcozo-upsell__card--compact' : ''}`;
    card.dataset.productHandle = product.handle;
    if (isPreview) card.dataset.needsHydration = '1';

    const featuredImage = product.featured_image || product.images?.[0] || '';

    // Variant selector: real products with multiple variants get a dropdown,
    // preview cards get a single hidden input that will be replaced on hydration
    const variantHtml = isPreview
      ? `<input type="hidden" data-variant-select value="" />`
      : (variants.length > 1
          ? renderVariantSelect(variants, compact)
          : `<input type="hidden" data-variant-select value="${firstAvailable.id}" />`);

    // Reusable price block — strikethrough only when discount actually lands here
    const priceBlockHtml = `
      <div class="marcozo-upsell__pricing">
        ${showStrikethrough ? `<span class="marcozo-upsell__price-original">${formatMoney(originalPrice)}</span>` : ''}
        <span class="marcozo-upsell__price-sale ${!showStrikethrough ? 'marcozo-upsell__price-sale--neutral' : ''}">${formatMoney(displayPrice)}</span>
      </div>
    `;

    // Unlock-savings copy for State 4 (chain-only cart adding a pendant)
    const unlockNoteHtml = showUnlockNote
      ? `<p class="marcozo-upsell__unlock">Unlocks $15 off your chain</p>`
      : '';

    if (compact) {
      card.innerHTML = `
        <img class="marcozo-upsell__image marcozo-upsell__image--compact"
             src="${featuredImage}" alt="${escapeHtml(product.title)}" loading="lazy" />
        <div class="marcozo-upsell__content marcozo-upsell__content--compact">
          <p class="marcozo-upsell__name marcozo-upsell__name--compact">${escapeHtml(product.title)}</p>
          ${priceBlockHtml}
          ${showUnlockNote ? '<p class="marcozo-upsell__unlock marcozo-upsell__unlock--compact">+$15 off your chain</p>' : ''}
        </div>
        ${variantHtml}
        <button type="button" class="marcozo-upsell__add" data-add-button
                aria-label="Add ${escapeHtml(product.title)} to cart">Add</button>
      `;
    } else {
      card.innerHTML = `
        <img class="marcozo-upsell__image"
             src="${featuredImage}" alt="${escapeHtml(product.title)}" loading="lazy" />
        <div class="marcozo-upsell__content">
          <p class="marcozo-upsell__name">
            ${escapeHtml(product.title)}
            ${showStrikethrough ? '<span class="marcozo-upsell__badge">Save $15</span>' : ''}
          </p>
          ${priceBlockHtml}
          ${unlockNoteHtml}
          <div class="marcozo-upsell__action">
            ${variantHtml}
            <button type="button" class="marcozo-upsell__add" data-add-button
                    aria-label="Add ${escapeHtml(product.title)} to cart">Add</button>
          </div>
        </div>
      `;
    }

    $('[data-add-button]', card).addEventListener('click', () => handleAddToCart(card, product, rec));
    return card;
  }

  function renderVariantSelect(variants, compact = false) {
    const options = variants.map(v => {
      const label = v.title === 'Default Title' ? (v.public_title || 'Default') : v.title;
      return `<option value="${v.id}" ${!v.available ? 'disabled' : ''}>${escapeHtml(label)}${!v.available ? ' (Sold out)' : ''}</option>`;
    }).join('');
    return `<select class="marcozo-upsell__variant${compact ? ' marcozo-upsell__variant--compact' : ''}" data-variant-select aria-label="Select option">${options}</select>`;
  }

  function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ============================================================
  // Add to cart
  // ============================================================

  async function handleAddToCart(card, product, rec) {
    const addBtn = $('[data-add-button]', card);
    const originalText = addBtn.textContent;
    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';

    let select = $('[data-variant-select]', card);
    let variantId = select?.value;

    // If this is a preview-only card and hasn't been hydrated yet,
    // fetch the full product on demand
    if ((!variantId || variantId === 'null') && product._isPreview) {
      log('Preview card add: hydrating', product.handle);
      const full = await fetchProductByHandle(product.handle);
      if (full) {
        rec.product = full;
        product = full;
        const firstAvailable = (full.variants || []).find(v => v.available) || full.variants?.[0];
        if (firstAvailable) variantId = firstAvailable.id;
      }
    }

    if (!variantId) {
      log('No variantId — aborting add');
      addBtn.textContent = 'Try again';
      addBtn.disabled = false;
      setTimeout(() => { addBtn.textContent = originalText; }, 2000);
      return;
    }

    try {
      const formData = {
        items: [{
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            '_bundle_upsell': 'cart_complete_look',
            '_bundle_source': rec.source,
            '_upsell_state': rec._cartState || 'unknown',
          },
        }],
      };

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      const addedItem = await res.json();

      if (rec.applyDiscount) await applyBundleDiscount();

      fireAnalytics('cart_upsell_added', {
        product_id: product.id,
        product_handle: product.handle,
        variant_id: variantId,
        price: addedItem.price,
        source: rec.source,
        cart_state: rec._cartState,
        discount_applied: rec.applyDiscount,
      });

      addBtn.textContent = '✓ Added';
      addBtn.classList.add('marcozo-upsell__add--success');
      setTimeout(() => refreshCartDrawer(), 250);
    } catch (err) {
      log('Add to cart error', err);
      addBtn.textContent = 'Try again';
      addBtn.disabled = false;
      setTimeout(() => { addBtn.textContent = originalText; }, 2000);
    }
  }

  async function applyBundleDiscount() {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: { '_bundle_discount_eligible': 'true' },
          discount: CONFIG.discountCode,
        }),
      });
    } catch (err) { log('applyBundleDiscount error', err); }
  }

  function refreshCartDrawer() {
    // Broadcast theme exposes:
    //   theme:cart:refresh (no payload) → triggers getCart() which fetches
    //     cart_url?section_id=api-cart-items and rebuilds drawer DOM
    //   theme:cart:change (REQUIRES payload with cartCount) — DON'T fire this manually
    //
    // We safely dispatch theme:cart:refresh — that rebuilds the drawer line items.
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));

    // Re-render OUR upsell after Broadcast's getCart() finishes (typically ~300-500ms)
    setTimeout(() => renderUpsell(), 600);
  }

  function fireAnalytics(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });

    // TriplePixel requires camelCase or specific naming — convert snake_case → camelCase
    if (window.TriplePixel) {
      try {
        const camelEvent = eventName.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        // TriplePixel only accepts whitelisted custom event names; wrap in try/catch
        window.TriplePixel('Custom', camelEvent, payload);
      } catch (err) { /* silent — TW pixel can reject custom events */ }
    }
    log('Analytics fired:', eventName, payload);
  }

  // ============================================================
  // Init
  // ============================================================

  async function renderUpsell() {
    const container = $('[data-marcozo-upsell]');
    if (!container) {
      warn('Upsell container [data-marcozo-upsell] not found in DOM. Snippet may not be rendered yet.');
      return;
    }
    const scrollEl = $('[data-marcozo-upsell-scroll]', container);
    if (!scrollEl) {
      warn('Scroll element not found inside container');
      return;
    }

    const cart = await fetchCart();
    if (!cart) {
      warn('Could not fetch /cart.js');
      container.hidden = true;
      return;
    }

    log('Cart contents:', cart.items.map(i => ({
      handle: i.handle,
      product_type: i.product_type,
      product_title: i.product_title,
      title: i.title,
      vendor: i.vendor,
    })));

    // Per-item classification debug
    cart.items.forEach((item, idx) => {
      const titleLower = (item.product_title || item.title || '').toLowerCase();
      const explicitChain = /\b(chain|cuban|franco|rope|figaro|box|snake|herringbone|curb)\b/.test(titleLower);
      log(`Item ${idx}: "${item.product_title}" — looksPendant=${looksLikePendant(item)}, looksChain=${looksLikeChain(item)}, explicitChain=${explicitChain}`);
    });

    const state = detectCartState(cart);
    log('Detected cart state:', state.state, 'pendant:', state.pendant?.handle, 'chain:', state.chain?.handle);

    if (state.state === 'empty') {
      container.hidden = true;
      return;
    }

    log('Loading recommendations for state:', state.state);
    const universalMap = getMap('marcozo-universal-map');
    log('Universal map keys:', Object.keys(universalMap).length);

    const recommendations = await buildRecommendations(state);
    log(`Built ${recommendations.length} recommendations:`, recommendations.map(r => ({
      handle: r.product.handle,
      source: r.source,
      applyDiscount: r.applyDiscount,
    })));

    if (recommendations.length === 0) {
      warn('No recommendations built. Either the co-purchase map has no entry for this pendant ' +
        'AND Shopify /recommendations/products.json returned no chains. ' +
        'Check that your product handles in cart-upsell.liquid JSON maps match real handles.');
      container.hidden = true;
      return;
    }

    recommendations.forEach(r => { r._cartState = state.state; });

    // Update copy by state — "Complete the Look" when set is incomplete (pendant XOR chain),
    // "You may also like" otherwise
    const titleEl = $('.marcozo-upsell__title', container);
    if (state.state === 'state1' || state.state === 'state4') {
      if (titleEl) titleEl.textContent = 'Complete the Look';
    } else {
      if (titleEl) titleEl.textContent = 'You may also like';
    }

    container.classList.toggle('marcozo-upsell--compact', !!state.compact);

    scrollEl.innerHTML = '';
    // Reset horizontal scroll to the start so user always sees the
    // top recommendation first (especially after add-to-cart re-renders)
    scrollEl.scrollLeft = 0;
    recommendations.forEach(rec => {
      const card = renderCard(rec, {
        compact: !!state.compact,
        applyDiscount: !!rec.applyDiscount,
      });
      if (card) scrollEl.appendChild(card);
    });

    container.hidden = false;
    log('✅ Upsell rendered (cards visible)');

    // After cards mount, ensure progress bar is bound (idempotent) and reflects
    // the actual scrollWidth. Cards stream in with image preloads, so we update
    // once on render and once on next animation frame to catch layout shifts.
    setupScrollProgress();
    updateScrollProgress();
    requestAnimationFrame(() => updateScrollProgress());

    fireAnalytics('cart_upsell_viewed', {
      cart_state: state.state,
      recommendation_count: recommendations.length,
      sources: recommendations.map(r => r.source),
      compact: !!state.compact,
    });

    // Background hydration: when /products/{handle}.js resolves for any
    // preview-only card, swap it in place to get real variant data.
    const hydrationPromises = recommendations._hydrationPromises || [];
    if (hydrationPromises.length) {
      hydrationPromises.forEach(p => {
        p.then(({ handle, full }) => {
          if (!full) return;
          // Find the rec entry and update it
          const rec = recommendations.find(r => r.product.handle === handle);
          if (!rec) return;
          rec.product = full;
          // Find and re-render that one card in place (preserves scroll position)
          const oldCard = scrollEl.querySelector(`[data-product-handle="${handle}"]`);
          if (oldCard) {
            const newCard = renderCard(rec, {
              compact: !!state.compact,
              applyDiscount: !!rec.applyDiscount,
            });
            if (newCard) oldCard.replaceWith(newCard);
          }
        });
      });
    }
  }

  // Debounce wrapper — prevents multiple back-to-back render calls
  let renderTimer = null;
  function scheduleRender(delay = 100) {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(() => renderUpsell(), delay);
  }

  function init() {
    // Preconnect hints — speeds up first product fetches by warming DNS/TLS
    if (!$('link[data-marcozo-preconnect]')) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = window.location.origin;
      link.setAttribute('data-marcozo-preconnect', '');
      document.head.appendChild(link);
    }

    // Pre-parse the universal map so first render isn't blocked by parse
    getMap('marcozo-universal-map');

    renderUpsell();

    // ============================================================
    // SCROLL PROGRESS BAR (True Classic-style)
    // Slim track + darker fill below the carousel that updates as
    // the user scrolls. Works on touch and mouse alike. No buttons —
    // pure visual cue that there's more content to swipe/drag to.
    // ============================================================
    setupScrollProgress();

    const refreshOn = [
      'theme:cart:refresh',
      'theme:cart:change',
      'theme:cart:update',
      'theme:cart:add',
      'theme:cart-drawer:open',
      'theme:cart-drawer:show',
      'theme:product:added',
    ];
    refreshOn.forEach(evt => {
      document.addEventListener(evt, () => scheduleRender(50));
    });
    // Scope observer to cart drawer/page only — not entire body
    const observerTargets = [
      $('cart-drawer'),
      $('[data-cart-drawer]'),
      $('main-cart-items'),
      $('[data-cart-form]'),
    ].filter(Boolean);
    if (observerTargets.length) {
      const observer = new MutationObserver(() => {
        const el = $('[data-marcozo-upsell]');
        const scroll = el && $('[data-marcozo-upsell-scroll]', el);
        if (el && scroll && scroll.children.length === 0) scheduleRender(50);
        // After any mutation that might add/remove cards, re-evaluate progress bar
        updateScrollProgress();
      });
      observerTargets.forEach(t => observer.observe(t, { childList: true, subtree: true }));
    }
  }

  // ============================================================
  // SCROLL PROGRESS HELPERS
  // ============================================================

  /**
   * Update progress bar fill width and position based on scroll state.
   * - Width of fill = (viewport / total) — represents proportion visible
   * - X position of fill = (scrollLeft / maxScroll) * (track - fillWidth)
   *   so fill slides left→right as user scrolls
   * - Bar hides entirely when content fits without overflow
   */
  function updateScrollProgress() {
    const scrollEl = $('[data-marcozo-upsell-scroll]');
    if (!scrollEl) return;
    const carousel = scrollEl.closest('[data-marcozo-carousel]');
    if (!carousel) return;
    const bar = $('[data-marcozo-progress]', carousel);
    const fill = $('[data-marcozo-progress-fill]', carousel);
    if (!bar || !fill) return;

    const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
    // No overflow → hide entire bar
    if (maxScroll <= 2) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;

    // Fill width represents fraction of content currently visible.
    // Floor at 20% so it stays visible even when carousel is mostly empty.
    const visibleRatio = scrollEl.clientWidth / scrollEl.scrollWidth;
    const fillPct = Math.max(20, Math.min(100, visibleRatio * 100));
    fill.style.width = fillPct + '%';

    // Slide fill horizontally as scroll progresses
    const scrollPct = scrollEl.scrollLeft / maxScroll;     // 0 → 1
    const trackWidth = bar.clientWidth;
    const fillWidth = (fillPct / 100) * trackWidth;
    const translateX = scrollPct * (trackWidth - fillWidth);
    fill.style.transform = `translateX(${translateX}px)`;
  }

  /**
   * One-time setup: bind scroll/resize listeners that drive progress bar,
   * plus pointer events for click-to-jump and drag-to-scrub interactivity.
   * Idempotent via dataset flag.
   */
  function setupScrollProgress() {
    const scrollEl = $('[data-marcozo-upsell-scroll]');
    if (!scrollEl || scrollEl.dataset.marcozoProgressBound === '1') return;

    const carousel = scrollEl.closest('[data-marcozo-carousel]');
    if (!carousel) return;
    const bar = $('[data-marcozo-progress]', carousel);
    if (!bar) return;

    // Update fill on user scroll (touch swipe, trackpad, drag)
    scrollEl.addEventListener('scroll', () => updateScrollProgress(), { passive: true });
    window.addEventListener('resize', () => updateScrollProgress());

    // ============================================================
    // CLICK-TO-JUMP / DRAG-TO-SCRUB
    // Map pointer X position on the bar → corresponding scrollLeft.
    // - Single click anywhere on track: jump that scroll position (smooth)
    // - Press + drag: scrub through carousel in real time (instant)
    // Uses Pointer Events so it works for mouse, touch, and pen alike.
    // ============================================================
    let isDragging = false;

    /**
     * Translate a pointer X coordinate (clientX) to a scrollLeft value.
     * Centers the visible viewport on the click point so the user feels
     * "I clicked there, that's where I am now."
     */
    function pointerToScrollLeft(clientX, smooth) {
      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
      if (maxScroll <= 0) return;
      scrollEl.scrollTo({
        left: ratio * maxScroll,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }

    bar.addEventListener('pointerdown', (e) => {
      // Ignore right-clicks and modifier-clicks
      if (e.button !== undefined && e.button !== 0) return;
      isDragging = true;
      bar.classList.add('is-dragging');
      bar.setPointerCapture(e.pointerId);
      // Single click → smooth jump; if user holds, subsequent moves are instant
      pointerToScrollLeft(e.clientX, true);
      e.preventDefault();
    });

    bar.addEventListener('pointermove', (e) => {
      if (!isDragging) return;
      pointerToScrollLeft(e.clientX, false); // instant tracking while dragging
    });

    function endDrag(e) {
      if (!isDragging) return;
      isDragging = false;
      bar.classList.remove('is-dragging');
      try { bar.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    bar.addEventListener('pointerup', endDrag);
    bar.addEventListener('pointercancel', endDrag);
    bar.addEventListener('pointerleave', endDrag);

    scrollEl.dataset.marcozoProgressBound = '1';
    updateScrollProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
